import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LayoutDashboard, Users, Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';
import { programRoutes } from '@/modules/shared/constants/routes';
import { useProgramRoleMenuPermissions } from '@/modules/programs/hooks/useProgramRoleMenuPermissions';
import { MENU_KEYS } from '@/modules/programs/services/programRoleMenuPermissionService';

interface WorkspaceTab {
  key: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  href: string;
  matchExact?: boolean;
}

/**
 * Horizontal workspace navigation tab strip.
 * Renders below the global header for role-scoped workspace users on
 * workspace and overview routes, replacing the per-page sidebar items
 * and reclaiming that sidebar width for operational content.
 */
export function WorkspaceNavTabs() {
  const location = useLocation();
  const navigate = useNavigate();
  const { programId: routeProgramId } = useParams<{ programId: string }>();
  const programId =
    routeProgramId || location.pathname.match(/\/programs\/([^/]+)/)?.[1];

  const { canAccessMenu } = useProgramRoleMenuPermissions({
    programId,
    enabled: !!programId,
    // Overview and My Work are opt-in per program — even SuperAdmin/ProductAdmin
    // must follow the program's real menu configuration for these two tabs.
    ignoreSuperAdminFor: [MENU_KEYS.OPERATIONAL_DASHBOARD, MENU_KEYS.MY_WORK],
  });

  if (!programId) return null;

  const tabs: WorkspaceTab[] = [];

  if (canAccessMenu(MENU_KEYS.OPERATIONAL_DASHBOARD)) {
    tabs.push({
      key: 'overview',
      label: 'Overview',
      Icon: LayoutDashboard,
      href: programRoutes.operationalDashboard(programId),
      matchExact: true,
    });
  }

  tabs.push({
    key: 'workspace',
    label: 'Patient Workspace',
    Icon: Users,
    href: programRoutes.workspace(programId),
  });

  if (canAccessMenu(MENU_KEYS.MY_WORK)) {
    tabs.push({
      key: 'my-work',
      label: 'My Work',
      Icon: Inbox,
      href: programRoutes.myWork(programId),
    });
  }

  // Single-item nav adds dead chrome — hide it entirely.
  if (tabs.length <= 1) return null;

  const isActive = (tab: WorkspaceTab): boolean => {
    if (tab.matchExact) return location.pathname === tab.href;
    return (
      location.pathname === tab.href ||
      location.pathname.startsWith(tab.href + '/')
    );
  };

  return (
    <nav
      aria-label="Workspace navigation"
      className="flex-shrink-0 flex items-center gap-0 border-b border-border bg-card overflow-x-auto scrollbar-hide px-4 lg:px-6"
    >
      {tabs.map((tab) => {
        const active = isActive(tab);
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => navigate(tab.href)}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2.5 text-[12px] font-medium whitespace-nowrap',
              'border-b-2 -mb-px transition-colors duration-150',
              active
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/40',
            )}
          >
            <tab.Icon className="h-3.5 w-3.5 flex-shrink-0" />
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}
